/**
 * Bookings Hub — the operational homepage for every persona.
 *
 * Founder direction (2026-07-26 · verbatim):
 *   "Booking System becomes Priority #1 (P0). It needs to become the
 *    primary operational hub with quick access to: today's lessons,
 *    upcoming bookings, availability, rescheduling, cancellations,
 *    driving tests, payments, outstanding actions, business reminders.
 *    When I log in, I should immediately understand what I need to do
 *    today."
 *
 * Sections (fixed order, above-the-fold first):
 *   1  Today's lessons                  (live cards, act on them)
 *   2  Outstanding actions              (missing evidence, unclosed lessons)
 *   3  Business reminders               (MOT, insurance, tax, service, docs)
 *   4  Upcoming (7 days)                (glance at the week)
 *   5  Pending booking requests         (accept/decline)
 *   6  Driving tests                    (dedicated list, always visible)
 *   7  Payments                         (paid / owed / overdue chips)
 *   8  Availability / reschedule links  (jump-off to planner + calendar)
 *
 * All data is pulled from the existing backend — no new endpoints.
 * Every row has a data-testid so the flow can be regression-tested.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Calendar as CalIcon, Clock, AlertTriangle, Bell, PoundSterling,
  ArrowRight, CheckCircle2, XCircle, RefreshCw, Car,
} from "lucide-react";
import { DarkModeToggle } from "../components/DarkModeToggle";
import { isPaid } from "../lib/paymentTruth";
import "./BookingsHub.css";

const API = process.env.REACT_APP_BACKEND_URL;
const OPTS = { withCredentials: true };

/* Small helpers -------------------------------------------------- */
const combineIso = (b) => {
  // Bookings from /api/bookings use separate `date` (YYYY-MM-DD) and
  // `time`/`start_time` (HH:MM) fields. Combine for JS Date parsing.
  if (b?.start_time && typeof b.start_time === "string" && b.start_time.includes("T")) return b.start_time;
  if (b?.starts_at) return b.starts_at;
  const d = b?.date;
  const t = b?.time || b?.start_time || "00:00";
  if (!d) return null;
  return `${d}T${t.length === 5 ? `${t}:00` : t}`;
};
const fmtTime = (b) => {
  const iso = combineIso(b);
  if (!iso) return "";
  try {
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) return b?.time || b?.start_time || "";
    return dt.toLocaleTimeString("en-GB", {
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return b?.time || b?.start_time || ""; }
};
const fmtDay = (b) => {
  const iso = combineIso(b);
  if (!iso) return "";
  try {
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) return b?.date || "";
    return dt.toLocaleDateString("en-GB", {
      weekday: "short", day: "numeric", month: "short",
    });
  } catch { return b?.date || ""; }
};
const isToday = (b) => {
  const iso = combineIso(b);
  if (!iso) return false;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) {
      // Fallback: date string compare
      const today = new Date().toISOString().slice(0, 10);
      return b?.date === today;
    }
    const now = new Date();
    return d.getFullYear() === now.getFullYear()
        && d.getMonth() === now.getMonth()
        && d.getDate() === now.getDate();
  } catch { return false; }
};
const isDrivingTest = (b) => {
  const s = `${b?.type || ""} ${b?.notes || ""} ${b?.summary || ""}`.toLowerCase();
  return s.includes("driving test") || s.includes("test day") || b?.category === "driving_test";
};
const paymentChip = (b) => {
  // Surface-2 Truth Hardening (Feb 2026): "Paid" is gated on the
  // authoritative `paid_at` timestamp (Stripe webhook / manual
  // mark-paid). `payment_status` enum alone is NOT sufficient
  // authority — see `lib/paymentTruth.js`.
  const paid = isPaid(b);
  const overdue = !paid && (b?.payment_status === "overdue" || b?.overdue);
  if (paid) return { label: "Paid", tone: "ok" };
  if (overdue) return { label: "Overdue", tone: "err" };
  return { label: "Owed", tone: "warn" };
};

export default function BookingsHub() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [upcoming, setUpcoming] = useState([]);
  const [pending, setPending] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [nudges, setNudges] = useState([]);
  const [optimisationPrefs, setOptimisationPrefs] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [preferenceStatus, setPreferenceStatus] = useState("");
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const results = await Promise.allSettled([
      axios.get(`${API}/api/bookings/upcoming`, OPTS),
      axios.get(`${API}/api/bookings/pending-requests`, OPTS),
      axios.get(`${API}/api/compliance/upcoming`, OPTS),
      axios.get(`${API}/api/reverse_intent/nudges`, OPTS),
      axios.get(`${API}/api/bookings/optimisation-preferences`, OPTS),
    ]);
    const [u, p, r, n, pref] = results;
    if (u.status === "fulfilled") {
      const data = u.value.data;
      setUpcoming(Array.isArray(data) ? data : (data?.bookings || data?.items || []));
    }
    if (p.status === "fulfilled") {
      const data = p.value.data;
      setPending(Array.isArray(data) ? data : (data?.requests || data?.items || []));
    }
    if (r.status === "fulfilled") {
      const data = r.value.data;
      setReminders(Array.isArray(data) ? data : (data?.items || data?.reminders || []));
    }
    if (n.status === "fulfilled") setNudges(n.value.data?.nudges || []);
    if (pref.status === "fulfilled") setOptimisationPrefs(pref.value.data);
    // Only show a hard error if every call blew up.
    if (results.every((x) => x.status === "rejected")) {
      setError("Couldn't load your bookings just now. Pull to refresh, or try again.");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const todaysLessons = upcoming.filter((b) => isToday(b));
  const drivingTests = upcoming.filter(isDrivingTest);
  const nonTestUpcoming = upcoming.filter((b) => !isDrivingTest(b));

  const acceptPending = useCallback(async (id) => {
    try {
      await axios.post(`${API}/api/bookings/${id}/accept`, {}, OPTS);
      load();
    } catch { /* ignore */ }
  }, [load]);
  const declinePending = useCallback(async (id) => {
    try {
      await axios.post(`${API}/api/bookings/${id}/decline`, {}, OPTS);
      load();
    } catch { /* ignore */ }
  }, [load]);

  const saveOptimisationPreferences = useCallback(async () => {
    if (!optimisationPrefs) return;
    setSavingPrefs(true);
    setPreferenceStatus("");
    try {
      const { data } = await axios.post(
        `${API}/api/bookings/optimisation-preferences`,
        {
          priority: optimisationPrefs.priority,
          search_days: optimisationPrefs.search_days,
        },
        OPTS,
      );
      setOptimisationPrefs((current) => ({ ...current, ...data }));
      setPreferenceStatus("Saved");
    } catch {
      setPreferenceStatus("Could not save");
    } finally {
      setSavingPrefs(false);
    }
  }, [optimisationPrefs]);

  return (
    <main className="bookings-hub" data-testid="bookings-hub-page">
      {/* HEADER -------------------------------------------------- */}
      <header className="bh-header">
        <div>
          <h1 className="bh-title">Bookings</h1>
          <p className="bh-sub">Everything you need to run today.</p>
        </div>
        <div className="bh-header__actions">
          <DarkModeToggle />
          <button
            type="button"
            className="bh-refresh"
            onClick={load}
            data-testid="bh-refresh"
            aria-label="Refresh"
            disabled={loading}
          >
            <RefreshCw aria-hidden="true" className={loading ? "bh-spin" : ""} />
          </button>
        </div>
      </header>

      {error && (
        <div className="bh-alert" role="alert" data-testid="bh-error">
          <AlertTriangle aria-hidden="true" /> <span>{error}</span>
        </div>
      )}

      {optimisationPrefs && (
        <section className="bh-section bh-optimisation" data-testid="bh-booking-optimisation">
          <div className="bh-section__head">
            <Car aria-hidden="true" />
            <h2>Smart booking priority</h2>
          </div>
          <p className="bh-empty">
            NDG uses this when suggesting a slot. It never books without confirmation.
          </p>
          <div className="bh-optimisation__controls">
            <label>
              Optimise for
              <select
                data-testid="bh-optimisation-priority"
                value={optimisationPrefs.priority}
                onChange={(event) => {
                  setPreferenceStatus("");
                  setOptimisationPrefs((current) => ({
                    ...current,
                    priority: event.target.value,
                  }));
                }}
              >
                <option value="least_driving">Least driving time</option>
                <option value="most_bookings">Most bookable lessons</option>
                <option value="largest_free_block">Largest free/home block</option>
                <option value="balanced">Balanced</option>
              </select>
            </label>
            <label>
              Search window
              <select
                data-testid="bh-optimisation-days"
                value={optimisationPrefs.search_days}
                onChange={(event) => {
                  setPreferenceStatus("");
                  setOptimisationPrefs((current) => ({
                    ...current,
                    search_days: Number(event.target.value),
                  }));
                }}
              >
                <option value={7}>Next 7 days</option>
                <option value={14}>Next 14 days</option>
                <option value={28}>Next 28 days</option>
              </select>
            </label>
            <button
              type="button"
              className="bh-optimisation__save"
              data-testid="bh-optimisation-save"
              onClick={saveOptimisationPreferences}
              disabled={savingPrefs}
            >
              {savingPrefs ? "Saving…" : "Save preference"}
            </button>
            {preferenceStatus && (
              <span className="bh-optimisation__status" role="status">
                {preferenceStatus}
              </span>
            )}
          </div>
        </section>
      )}

      {/* 1 · TODAY'S LESSONS -------------------------------------- */}
      <section className="bh-section bh-section--primary" data-testid="bh-today">
        <div className="bh-section__head">
          <Clock aria-hidden="true" />
          <h2>Today&apos;s lessons</h2>
          <span className="bh-count" data-testid="bh-today-count">{todaysLessons.length}</span>
        </div>
        {todaysLessons.length === 0 ? (
          <p className="bh-empty">Nothing on the diary for today.</p>
        ) : (
          <ul className="bh-list">
            {todaysLessons.map((b) => (
              <li
                key={b.id || b._id}
                className="bh-card"
                data-testid={`bh-today-lesson-${b.id || b._id}`}
              >
                <div className="bh-card__time">{fmtTime(b)}</div>
                <div className="bh-card__body">
                  <strong>{b.pupil_name || b.name || "Lesson"}</strong>
                  <span className="bh-card__sub">
                    {b.duration_minutes || b.duration ? `${b.duration_minutes || b.duration} min · ` : ""}
                    {b.pickup_location || b.location || "Standard pickup"}
                  </span>
                </div>
                <div className="bh-card__actions">
                  <span className={`bh-chip bh-chip--${paymentChip(b).tone}`}>
                    <PoundSterling aria-hidden="true" /> {paymentChip(b).label}
                  </span>
                  <button
                    type="button"
                    onClick={() => nav(`/lesson/${b.id || b._id}`)}
                    data-testid={`bh-today-open-${b.id || b._id}`}
                  >
                    Open <ArrowRight aria-hidden="true" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 2 · OUTSTANDING ACTIONS ---------------------------------- */}
      <section className="bh-section" data-testid="bh-actions">
        <div className="bh-section__head">
          <AlertTriangle aria-hidden="true" />
          <h2>Outstanding actions</h2>
          <span className="bh-count">{nudges.length}</span>
        </div>
        {nudges.length === 0 ? (
          <p className="bh-empty">Nothing waiting on you. Nice.</p>
        ) : (
          <ul className="bh-list">
            {nudges.map((n) => (
              <li
                key={n.id}
                className={`bh-card bh-card--${n.severity}`}
                data-testid={`bh-nudge-${n.trigger_id}`}
              >
                <div className="bh-card__body">
                  <strong>{n.subject_summary}</strong>
                </div>
                <button
                  type="button"
                  onClick={() => nav(n.suggested_action_intent?.route || "/upload")}
                  data-testid={`bh-nudge-act-${n.trigger_id}`}
                >
                  {n.suggested_action_text || "Fix"} <ArrowRight aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 3 · BUSINESS REMINDERS ----------------------------------- */}
      <section className="bh-section" data-testid="bh-reminders">
        <div className="bh-section__head">
          <Bell aria-hidden="true" />
          <h2>Business reminders</h2>
          <span className="bh-count">{reminders.length}</span>
        </div>
        {reminders.length === 0 ? (
          <p className="bh-empty">No MOT, insurance, tax or service items due soon.</p>
        ) : (
          <ul className="bh-list">
            {reminders.slice(0, 6).map((r, i) => (
              <li key={r.id || i} className="bh-card" data-testid={`bh-reminder-${i}`}>
                <div className="bh-card__body">
                  <strong>{r.label || r.type || r.name || "Reminder"}</strong>
                  <span className="bh-card__sub">
                    {r.due_date || r.date || r.expires_at}
                  </span>
                </div>
                <button type="button" onClick={() => nav("/compliance")}>Open</button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 4 · UPCOMING --------------------------------------------- */}
      <section className="bh-section" data-testid="bh-upcoming">
        <div className="bh-section__head">
          <CalIcon aria-hidden="true" />
          <h2>Upcoming</h2>
          <span className="bh-count">{nonTestUpcoming.length}</span>
        </div>
        {nonTestUpcoming.length === 0 ? (
          <p className="bh-empty">The diary is clear for the next week.</p>
        ) : (
          <ul className="bh-list">
            {nonTestUpcoming.slice(0, 10).map((b) => (
              <li
                key={b.id || b._id}
                className="bh-card"
                data-testid={`bh-upcoming-${b.id || b._id}`}
              >
                <div className="bh-card__time">
                  {fmtDay(b)}
                  <span>{fmtTime(b)}</span>
                </div>
                <div className="bh-card__body">
                  <strong>{b.pupil_name || b.name || "Lesson"}</strong>
                  <span className="bh-card__sub">
                    {b.duration_minutes || b.duration ? `${b.duration_minutes || b.duration} min` : ""}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => nav(`/lesson/${b.id || b._id}`)}
                  data-testid={`bh-upcoming-open-${b.id || b._id}`}
                >
                  Open <ArrowRight aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 5 · PENDING REQUESTS ------------------------------------- */}
      {pending.length > 0 && (
        <section className="bh-section" data-testid="bh-pending">
          <div className="bh-section__head">
            <Bell aria-hidden="true" />
            <h2>Pending booking requests</h2>
            <span className="bh-count">{pending.length}</span>
          </div>
          <ul className="bh-list">
            {pending.map((b) => (
              <li key={b.id || b._id} className="bh-card" data-testid={`bh-pending-${b.id || b._id}`}>
                <div className="bh-card__body">
                  <strong>{b.pupil_name || b.name}</strong>
                  <span className="bh-card__sub">
                    {fmtDay(b)} · {fmtTime(b)}
                  </span>
                </div>
                <div className="bh-card__actions">
                  <button
                    type="button"
                    className="bh-btn bh-btn--ok"
                    onClick={() => acceptPending(b.id || b._id)}
                    data-testid={`bh-pending-accept-${b.id || b._id}`}
                  >
                    <CheckCircle2 aria-hidden="true" /> Accept
                  </button>
                  <button
                    type="button"
                    className="bh-btn bh-btn--err"
                    onClick={() => declinePending(b.id || b._id)}
                    data-testid={`bh-pending-decline-${b.id || b._id}`}
                  >
                    <XCircle aria-hidden="true" /> Decline
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* 6 · DRIVING TESTS ---------------------------------------- */}
      <section className="bh-section" data-testid="bh-tests">
        <div className="bh-section__head">
          <Car aria-hidden="true" />
          <h2>Driving tests</h2>
          <span className="bh-count">{drivingTests.length}</span>
        </div>
        {drivingTests.length === 0 ? (
          <p className="bh-empty">No driving tests scheduled.</p>
        ) : (
          <ul className="bh-list">
            {drivingTests.map((b) => (
              <li key={b.id || b._id} className="bh-card" data-testid={`bh-test-${b.id || b._id}`}>
                <div className="bh-card__time">
                  {fmtDay(b)}
                  <span>{fmtTime(b)}</span>
                </div>
                <div className="bh-card__body">
                  <strong>{b.pupil_name || b.name || "Driving test"}</strong>
                  <span className="bh-card__sub">{b.test_centre || b.location || ""}</span>
                </div>
                <button
                  type="button"
                  onClick={() => nav(`/lesson/${b.id || b._id}`)}
                  data-testid={`bh-test-open-${b.id || b._id}`}
                >
                  Open <ArrowRight aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 7 · JUMP-OFFS -------------------------------------------- */}
      <section className="bh-section bh-jumpoffs" data-testid="bh-jumpoffs">
        <button type="button" onClick={() => nav("/work-mode")} data-testid="bh-jump-add-pupil">
          <strong>Add pupil</strong>
          <span>Create their record and invitation</span>
        </button>
        <button type="button" onClick={() => nav("/calendar-planner")} data-testid="bh-jump-availability">
          <strong>Availability & working hours</strong>
          <span>Open the calendar planner</span>
        </button>
        <button type="button" onClick={() => nav("/instructor/today")} data-testid="bh-jump-admin-bookings">
          <strong>Diary & rescheduling</strong>
          <span>Open your working diary</span>
        </button>
        <button type="button" onClick={() => nav("/recurring-booking")} data-testid="bh-jump-recurring">
          <strong>Recurring lessons</strong>
          <span>Weekly series and pauses</span>
        </button>
      </section>
    </main>
  );
}
